type IssuePageUrlInput = {
  primaryUrl?: string | null;
  canonicalPageUrl?: string | null;
  propertyBaseUrl?: string | null;
};

const trimUrl = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isHttpUrl = (url: URL): boolean => url.protocol === 'http:' || url.protocol === 'https:';

export const safeHttpUrl = (value: string | null | undefined): string | null => {
  const trimmed = trimUrl(value);
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);
    return isHttpUrl(parsed) ? trimmed : null;
  } catch {
    return null;
  }
};

const parseHttpUrl = (value: string | null | undefined): URL | null => {
  const trimmed = trimUrl(value);
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    return isHttpUrl(url) ? url : null;
  } catch {
    return null;
  }
};

const resolveRelativeUrl = (value: string, baseUrl: URL | null): string | null => {
  if (!baseUrl) return null;

  try {
    const base = value.startsWith('?') || value.startsWith('#') ? baseUrl.href : `${baseUrl.origin}/`;
    const resolved = new URL(value, base);
    return isHttpUrl(resolved) ? resolved.href : null;
  } catch {
    return null;
  }
};

export function resolveIssuePageUrl({
  primaryUrl,
  canonicalPageUrl,
  propertyBaseUrl,
}: IssuePageUrlInput): string | null {
  const primary = trimUrl(primaryUrl);
  if (!primary) {
    return parseHttpUrl(canonicalPageUrl)?.href ?? null;
  }

  const absolutePrimary = parseHttpUrl(primary);
  if (absolutePrimary) {
    return absolutePrimary.href;
  }

  const canonicalBaseUrl = parseHttpUrl(canonicalPageUrl);
  const propertyBase = parseHttpUrl(propertyBaseUrl);
  return resolveRelativeUrl(primary, canonicalBaseUrl) ?? resolveRelativeUrl(primary, propertyBase);
}

export function getIssuePageDisplayUrl(input: IssuePageUrlInput): string | null {
  return (
    resolveIssuePageUrl(input) ??
    trimUrl(input.primaryUrl) ??
    trimUrl(input.canonicalPageUrl) ??
    null
  );
}
